import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import isNil from 'lodash/isNil';
import set from 'lodash/set';
import * as urlUtils from 'privates/urlUtils';
import * as utils from 'privates/utils';
import createEventFrom from 'privates/eventService';
import platformUtils from 'platformUtils';
import reporter from "./reporter";
import {generateError} from "./validations/errors";

const Intent = {
    WIX_CODE_SITE_API: 'WIX_CODE_SITE_API',
    WIX_CODE_APP_API: 'WIX_CODE_APP_API',
    WIX_CODE_RESPONSE: 'WIX_CODE_RESPONSE',
    WIX_CODE: 'WIX_CODE',
    WIX_CODE_WARMUP: 'WIX_CODE_WARMUP',
    BROWSER_API: 'BROWSER_API'
};

const MessageTypes = {
    NAVIGATE_TO: 'navigateTo',
    SITE_INFO: 'siteInfo',
    SM_CURRENT_USER_EMAIL: 'smCurrentUserEmail',
    SM_REQUEST_LOGIN: 'smRequestLogin',
    SM_LOGOUT: 'smLogout',
    SM_FORGOT_PASSWORD: 'smForgotPassword',
    WINDOW_BOUNDING_RECT: 'windowBoundingRect',
    SCROLL_TO: 'scrollTo',
    SCROLL_TO_COMP: 'scrollToComponent',
    SCROLL_BY: 'scrollBy',
    GET_APP_API: 'getAppAPI',
    INVOKE_APP_FN: 'invokeAppFn',
    INVOKE_APP_ON: 'invokeAppOn',
    SET_TO_STORAGE: 'setToStorage',
    STORAGE_UPDATE: 'storageWasUpdated',
    POST_HTML_MESSAGE: 'postHtmlMessage',
    OPEN_LIGHTBOX: 'openLightbox',
    CLOSE_LIGHTBOX: 'closeLightbox',
    START_UPLOAD: 'startFileUpload',
    GET_ROUTERS_SITEMAP: 'getRoutersSiteMap',
    GET_CURRENT_GEOLOCATION: 'getCurrentGeolocation',
    SET_WARMUP_DATA: 'setWarmupData',
    GOOGLE_ANALYTICS: 'googleAnalytics',
    TRACK_EVENT: 'trackEvent',
    OPEN_MODAL: 'openModal',
    SEND_MESSAGE_TO_PARENT: 'sendMessageToParent'
};

let callbackId = 1;

const registeredEventListeners = {};

const callbacks = {};

const postMessage = (message, ports) => {
    const args = [message];
    if (!utils.isWorker()) {
        args.push('*');
    }
    if (ports) {
        args.push(ports);
    }
    self.postMessage(...args);
};

const msgSenders = {
    WIX_CODE_WARMUP: sendWarmupCommandMessage,
    WIX_CODE: sendCompCommandMessage,
    BROWSER_API: sendBrowserCommandMessage,
    WIX_CODE_SITE_API: sendGeneralViewerCommandMessage,
    WIX_CODE_APP_API: sendGeneralViewerCommandMessage
};

const EventsCallbacks = {};

function sendMessage (intent, ...args) {
    (msgSenders[intent] || sendGeneralViewerCommandMessage)(intent, ...args);
}

function sendBrowserCommandMessage (intent, msgType, params) {
    if (!msgType) {
        return;
    }

    postMessage({
        intent,
        type: msgType,
        data: params
    });
}

function sendGeneralViewerCommandMessage (intent, msgType, params, callback) {
    if (!msgType) {
        return;
    }

    const callbackId = getCallbackId();
    if (callback) {
        callbacks[callbackId] = callback;
    }

    postMessage({
        callbackId,
        intent,
        type: msgType,
        contextId: platformUtils.getContextId(),
        deviceType: urlUtils.getQueryParameter('deviceType') || 'desktop',
        data: params
    });
}

function sendWarmupCommandMessage(intent, command, params) {
    intent = 'WIX_CODE';
    try {
        postMessage({
            intent,
            type: 'wix_code_warmup_data',
            deviceType: urlUtils.getQueryParameter('deviceType') || 'desktop',
            data: params,
            command
        });
    } catch(error) {
        reporter.reportSdkError(generateError.error_post_message({error}));
    }
}

function sendCompCommandMessage (intent, command, compId, contextId, params, callback) {
    //validations
    const callbackId = getCallbackId();
    if (callback) {
        callbacks[callbackId] = callback;
    }

    postMessage({
        callbackId,
        intent,
        type: 'wix_code_iframe_command',
        deviceType: urlUtils.getQueryParameter('deviceType') || 'desktop',
        data: params,
        compId,
        contextId,
        command
    });
}

function onMessage (event) {
    if (!event || !event.data) {
        return;
    }

    let data = {};
    try {
        if (isString(event.data)) {
            data = JSON.parse(event.data);
        } else if (isObject(event.data)) {
            data = event.data;
        }
    } catch(e) {
        return;
    }

    switch(data.intent) {
        case 'WIX_CODE_RESPONSE':
            const callback = data.callbackId && callbacks[data.callbackId];
            if (callback) {
                if (!isNil(data.result)) {
                    let dataToPassToCallback = data.result;
                    if(data.type === MessageTypes.INVOKE_APP_ON) {
                        dataToPassToCallback = createEventFrom(data.result);
                    } else if(data.result.command === 'propsChanged') {
                        dataToPassToCallback = null;
                    }
                    callback.call(this, dataToPassToCallback);
                } else {
                    callback(data.result, data.error);
                }

                if (data.type !== MessageTypes.INVOKE_APP_ON) {
                    delete callbacks[data.callbackId];
                }
            }
            break;

        case 'addEventListener':
            callEventListeners(data);
            break;

        case 'WIX_CODE':
            const RGI = platformUtils.getRGI();

            if (data.type === 'update_site_member') {
                RGI.addSiteMemberData(data.updates);
                break;
            }

            if (data.type === 'update_apps_data') {
                RGI.addAppsData(data.updates);
                break;
            }

            if (data.type === 'update_navigation') {
                RGI.addNavigation(data.updates);
                break;
            }

            if (data.type === 'update_session_info') {
                RGI.addSessionInfoProp(data.updates);
                break;
            }

            let RMI;
            try {
                RMI = platformUtils.getRMI();
            } catch (e) {
                return; //Ignore events before we have a model in here.
            }

            if (data.type === 'update') {
                RMI.updateModel(data.updates);
                break;
            }

            // TODO: should have a check for data.type
            const funcRef = data.callbackId;
            const compId = data.compId;
            const dynamicCallback = RMI.getCallbackById(funcRef, true);
            const staticCallback = platformUtils.getStaticEventHandler(funcRef);
            const {itemId} = RMI.getRepeaterItemContext(compId);

            if (itemId) {
                set(data, 'event.context', {itemId});
            }

            if (dynamicCallback) {
                const selector = platformUtils.getWixSelector().getScopedSelector(compId, dynamicCallback.contextId, dynamicCallback.controllerId);
                dynamicCallback.callback.call(this, createEventFrom(data, dynamicCallback.controllerId), selector);
            }
            if (staticCallback) {

                const selector = platformUtils.getWixSelector().getScopedSelector(compId, staticCallback.contextId, staticCallback.controllerId);
                staticCallback.callback.call(this, createEventFrom(data, staticCallback.controllerId), selector);
            }

            break;
    }

    if (registeredEventListeners[data.type]) {
        registeredEventListeners[data.type](data);
    }
}

function registerEventListener(msgType, listener) {
    registeredEventListeners[msgType] = listener;
}

function unregisterEventListener(msgType) {
    delete registeredEventListeners[msgType];
}

function callEventListeners(data, typeOfCall){
    if (EventsCallbacks[data.eventType]) {
        EventsCallbacks[data.eventType].forEach(function (callbackHandler) {
            callbackHandler.callback.call(this, data.params, typeOfCall);
        });
    }
}

function getCallbackId () {
    return callbackId++;
}

export {
    sendMessage,
    MessageTypes,
    Intent,
    callEventListeners,
    onMessage,
    registerEventListener,
    unregisterEventListener
};



// WEBPACK FOOTER //
// ./js/modules/privates/postMessage.es6